/**
 * Per-user organization UI preferences in localStorage (last selected + custom sort order).
 */

function lastOrgStorageKey(userId: string): string {
  return `pm:lastOrg:${userId}`;
}

function orgOrderStorageKey(userId: string): string {
  return `pm:orgOrder:${userId}`;
}

export function getStoredLastOrgId(userId: string | undefined): string | null {
  if (!userId) return null;
  try {
    return localStorage.getItem(lastOrgStorageKey(userId));
  } catch {
    return null;
  }
}

export function setStoredLastOrgId(userId: string | undefined, orgId: string): void {
  if (!userId) return;
  try {
    localStorage.setItem(lastOrgStorageKey(userId), orgId);
  } catch {
    /* ignore quota / private mode */
  }
}

export function getStoredOrgOrder(userId: string | undefined): string[] {
  if (!userId) return [];
  try {
    const raw = localStorage.getItem(orgOrderStorageKey(userId));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    return Array.isArray(parsed) ? parsed.map((id) => String(id)) : [];
  } catch {
    return [];
  }
}

export function setStoredOrgOrder(userId: string | undefined, order: string[]): void {
  if (!userId) return;
  try {
    localStorage.setItem(orgOrderStorageKey(userId), JSON.stringify(order));
  } catch {
    /* ignore */
  }
}

export interface OrgPrefItem {
  _id: string;
  name?: string;
}

/** Drop unknown ids from saved order, append new org ids at end (stable for new companies). */
export function reconcileOrgOrder(savedOrder: string[], currentOrgIds: string[]): string[] {
  const idSet = new Set(currentOrgIds);
  const filtered = savedOrder.filter((id) => idSet.has(id));
  const placed = new Set(filtered);
  for (const id of currentOrgIds) {
    if (!placed.has(id)) {
      filtered.push(id);
      placed.add(id);
    }
  }
  return filtered;
}

export function sortOrganizationsByUserOrder<T extends OrgPrefItem>(orgs: T[], savedOrder: string[]): T[] {
  const byId = new Map(orgs.map((o) => [o._id, o]));
  const ordered: T[] = [];
  const seen = new Set<string>();
  for (const id of savedOrder) {
    const org = byId.get(id);
    if (org) {
      ordered.push(org);
      seen.add(id);
    }
  }
  const rest = orgs
    .filter((o) => !seen.has(o._id))
    .sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }));
  return [...ordered, ...rest];
}
